import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import Typography from 'app/ui/components/atoms/typography';
import SeeAllLink from 'app/ui/components/atoms/see-all-link';

import type { ProgressivePlan, ProgressivePlanInstance } from 'app/types/graphql';

import EmptyState from 'app/ui/components/molecules/empty-state';
import GhostIcon from 'app/ui/components/atoms/ghost';
import StartPlanBanner from 'app/ui/components/molecules/library-training-plans/start-plan-banner';
import ActiveTrainingPlanRail from 'app/ui/components/molecules/active-training-plan-rail';
import { routes } from 'app/routes/constants';

import Tooltip from 'app/ui/components/molecules/tooltip';
import { TitleWithTooltip } from 'app/ui/components/molecules/tooltip/util';
import QuestionMarkCircle from 'app/ui/components/atoms/icons/question-mark-circle';

export type TitleVariant = 'h3' | 'h4' | 'h5';
type LibraryTrainingPlansProps = {
  titleVariant?: TitleVariant;
  planInstances: ProgressivePlanInstance[];
  plans: Pick<ProgressivePlan, 'id'>[];
};

export const TrainingPlansArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;
  gap: 1.5rem;
  margin-bottom: 6rem;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const PageTitle = styled(Typography)`
  margin: 0;
`;

const ActivePlanWrapper = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  &:last-child {
    margin-bottom: 0;
  }
`;

const PlanTitle = styled(Typography)`
  margin: 0 0 -${({ theme }) => theme.spacing(1)} 0;
`;

/** Whether the "See all" link is shown, only takes up its space (the skeleton), or is left out. */
export type SeeAllState = 'visible' | 'reserved' | 'hidden';

// Reserving the link's box rather than omitting it keeps the title row identical between the
// skeleton and the loaded section; `visibility: hidden` also drops it from the tab order.
const SeeAllSlot = styled.div<{ $reserved: boolean }>`
  visibility: ${({ $reserved }) => ($reserved ? 'hidden' : 'visible')};
`;

type LibraryTrainingPlansHeaderProps = {
  titleVariant: TitleVariant;
  seeAll?: SeeAllState;
};

/** The section's title row and copy; shared with the skeleton so the header is final before the plans arrive. */
export const LibraryTrainingPlansHeader: React.FC<LibraryTrainingPlansHeaderProps> = ({
  titleVariant,
  seeAll = 'hidden',
}) => {
  const theme = useTheme();
  const intl = useIntl();

  return (
    <TitleWrapper>
      <TitleRow>
        <TitleWithTooltip>
          <PageTitle variant={titleVariant} color={theme.colors.white}>
            {intl.formatMessage({ id: 'library.trainingPlans' })}
          </PageTitle>
          <Tooltip tooltipText={intl.formatMessage({ id: 'trainingPlan.tooltip.header' })} usePortal={true}>
            <QuestionMarkCircle />
          </Tooltip>
        </TitleWithTooltip>
        {seeAll !== 'hidden' && (
          <SeeAllSlot $reserved={seeAll === 'reserved'} aria-hidden={seeAll === 'reserved'}>
            <SeeAllLink to={routes.TRAINING_PLANS}>{intl.formatMessage({ id: 'library.seeAll' })}</SeeAllLink>
          </SeeAllSlot>
        )}
      </TitleRow>
      <Typography variant="body2" color={theme.colors.lightGray}>
        {intl.formatMessage({ id: 'library.trainingPlansCopy' })}
      </Typography>
    </TitleWrapper>
  );
};

const LibraryTrainingPlans: React.FC<LibraryTrainingPlansProps> = ({ titleVariant = 'h3', planInstances, plans }) => {
  const theme = useTheme();
  const intl = useIntl();

  const hasActiveInstances = planInstances.length > 0;
  const hasPlans = plans.length > 0;

  return (
    <TrainingPlansArea>
      <LibraryTrainingPlansHeader titleVariant={titleVariant} seeAll={plans.length > 1 ? 'visible' : 'hidden'} />
      {hasActiveInstances ? (
        planInstances.map((planInstance) => (
          <ActivePlanWrapper key={planInstance.id}>
            <PlanTitle variant="h5" color={theme.colors.white}>
              {planInstance.progressivePlan.name}
            </PlanTitle>
            <ActiveTrainingPlanRail planInstance={planInstance as ProgressivePlanInstance} />
          </ActivePlanWrapper>
        ))
      ) : hasPlans ? (
        <StartPlanBanner planId={plans[0].id} />
      ) : (
        <EmptyState icon={<GhostIcon />} heading={intl.formatMessage({ id: 'library.noTrainingPlans' })} />
      )}
    </TrainingPlansArea>
  );
};

export default LibraryTrainingPlans;
